import { Box, Grid, Typography, useTheme } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { DataGrid } from "components/DataGrid";
import CopyableTypography from "components/Typography/CopyableTypography";
import { GetCohortQuery } from "generated-graphql/graphql";
import { groupBy, startCase } from "lodash-es";
import { useMemo } from "react";

type CohortData = NonNullable<GetCohortQuery["cohort"]>;

function toTitleCase(input: string): string {
  if (input.startsWith("is")) {
    return startCase(input.slice(2)) + "?";
  }

  return startCase(input);
}

interface CohortStateFieldsProps {
  cohort: CohortData;
}

export function CohortStateFields({ cohort }: CohortStateFieldsProps) {
  const theme = useTheme();

  const stateFields = useMemo(
    () =>
      cohort.members.flatMap((member) => member.facility?.stateFields ?? []),
    [cohort.members]
  );

  const stateFieldsGroupedByKey = useMemo(
    () => groupBy(stateFields, "key"),
    [stateFields]
  );

  const stateFieldsGroupedByKeyArray = useMemo(
    () =>
      Object.entries(stateFieldsGroupedByKey).map(([key, fields]) => {
        // Combine filter and map in a single pass, and deduplicate in one loop
        const seen = new Set<string>();
        const uniqueValues: string[] = [];

        for (const field of fields) {
          const val = field.value;
          // Skip null/undefined values early
          if (val === undefined || val === null) continue;

          // For primitives, use direct comparison; for objects, stringify
          // This avoids expensive JSON.stringify for simple values
          const stringified =
            typeof val === "object" ? JSON.stringify(val) : String(val);

          if (!seen.has(stringified)) {
            seen.add(stringified);
            uniqueValues.push(stringified);
          }
        }

        return {
          key,
          value: uniqueValues.join(", "),
        };
      }),
    [stateFieldsGroupedByKey]
  );

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        flex: 1,
        field: "key",
        valueGetter: (_, row) => toTitleCase(row.key),
        headerName: "Question",
      },
      {
        flex: 1,
        field: "value",
        headerName: "Answer",
        verticalAlign: "center",
        renderCell: (params) => {
          return (
            <CopyableTypography
              text={params.row.value}
              variant="body2"
              sx={{ lineHeight: "inherit" }}
            />
          );
        },
      },
    ],
    []
  );

  return (
    <Box
      sx={{
        mt: 3,
        pt: 2,
        borderTop: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500 }}>
        Facility State Fields
      </Typography>

      <Grid container spacing={3}>
        <DataGrid
          columns={columns}
          rows={stateFieldsGroupedByKeyArray}
          getRowId={(row) => row.key}
          isRowSelectable={() => false}
          pagination
          autoHeight
          initialState={{
            sorting: {
              sortModel: [
                {
                  field: "value",
                  sort: "desc",
                },
                { field: "key", sort: "asc" },
              ],
            },
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          pageSizeOptions={[10, 15, 50]}
          sx={{
            width: "100%",
            "& .MuiDataGrid-root": {
              width: "100%",
            },
            "& .MuiDataGrid-virtualScroller": {
              minHeight: "200px",
            },
            "& .MuiDataGrid-columnHeaders": {
              minHeight: "auto",
            },
            "& .MuiDataGrid-cell": {
              whiteSpace: "normal",
              wordWrap: "break-word",
            },
          }}
        />
      </Grid>
    </Box>
  );
}
